import type { AppResponseChatSessionCountsResponse, ChatSessionCountsResponse } from "@agent-service-types/index.ts";
import type { UseQueryOptions } from "@tanstack/vue-query";
import { useQuery } from "@tanstack/vue-query";
import type { AxiosResponse } from "axios";
import type { MaybeRef } from "vue";
import { computed, toValue } from "vue";
import { agentServiceRequest } from "@/services/utils";

interface ChatSessionCountsParams {
  userOktaId?: string | null;
  externalChatSessionId?: string | null;
  title?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  textFeedback?: string | null;
  ratingFeedback?: number | null;
  isAnonymous?: boolean | null;
  orderBy?: "title" | "first_name" | "last_name" | "email" | "text_feedback" | "rating_feedback" | "created_at" | null;
  orderDirection?: "asc" | "desc" | null;
  limit?: number;
  offset?: number;
}

export const getChatSessionCountsQueryKey = (params?: MaybeRef<ChatSessionCountsParams | undefined>) => {
  const p = toValue(params);
  return p !== undefined ? ["get-chat-session-counts", p] : ["get-chat-session-counts"];
};

export const useGetChatSessionCounts = <TData = ChatSessionCountsResponse>(
  params?: MaybeRef<ChatSessionCountsParams | undefined>,
  useQueryOptions?: Partial<UseQueryOptions<ChatSessionCountsResponse, Error, TData>>,
) => {
  const queryKey = computed(() => getChatSessionCountsQueryKey(params));

  return useQuery<ChatSessionCountsResponse, Error, TData>({
    queryKey,
    queryFn: async (): Promise<ChatSessionCountsResponse> => {
      const p = toValue(params);
      const response: AxiosResponse<AppResponseChatSessionCountsResponse> = await agentServiceRequest({
        method: "get",
        url: "/chat_sessions/counts",
        params: {
          user_okta_id: p?.userOktaId,
          external_chat_session_id: p?.externalChatSessionId,
          title: p?.title,
          first_name: p?.firstName,
          last_name: p?.lastName,
          email: p?.email,
          text_feedback: p?.textFeedback,
          rating_feedback: p?.ratingFeedback,
          is_anonymous: p?.isAnonymous,
          order_by: p?.orderBy,
          order_direction: p?.orderDirection,
          limit: p?.limit,
          offset: p?.offset,
        },
      });
      return response.data.data!;
    },
    ...useQueryOptions,
  });
};
